<template>
    <div 
        ref="container" 
        class="composition" 
        :class="{ 'crosshair': isToolSketch || isToolMarker || isToolErase }"
        @pointerdown="handlePointerDown" 
        @pointermove="handlePointerMove" 
        @pointerup="handlePointerUp"
        @pointercancel="handlePointerUp" 
        @pointerleave="handlePointerLeave" 
        @pointerenter="handlePointerEnter">

        <div 
            v-if="showToolPreview && !cursor.active" 
            class="tool-preview" 
            :class="{ 'fade-out': toolPreviewFading, 'marker': isToolMarker, 'erase': isToolErase }"
            :style="toolPreviewStyle">
        </div>

        <CompositionLayer 
            ref="image" 
            class="layer image" 
            :resolution="defaultResolution"
            @change="handleLayerChange('image', $event)" />
        <CompositionLayer 
            ref="marker" 
            class="layer marker" 
            :resolution="defaultResolution"
            @change="handleLayerChange('marker', $event)" />
        <CompositionLayer 
            ref="sketch" 
            class="layer sketch" 
            :resolution="defaultResolution"
            @change="handleLayerChange('sketch', $event)" />

        <div 
            v-if="isToolCrop" 
            ref="cropOverlay" 
            class="crop-overlay" 
            @pointerdown.stop.prevent="cropPointerDown"
            @pointermove.stop.prevent="cropPointerMove" 
            @pointerup.stop.prevent="cropPointerUp"
            @pointercancel.stop.prevent="cropPointerUp">

            <div class="crop-rect" :style="cropRectStyle">
                <div class="grid"></div>
                <div class="handle handle-nw" @pointerdown.stop.prevent="handleResizePointerDown('nw', $event)"></div>
                <div class="handle handle-ne" @pointerdown.stop.prevent="handleResizePointerDown('ne', $event)"></div>
                <div class="handle handle-sw" @pointerdown.stop.prevent="handleResizePointerDown('sw', $event)"></div>
                <div class="handle handle-se" @pointerdown.stop.prevent="handleResizePointerDown('se', $event)"></div>
                <div class="handle handle-n" @pointerdown.stop.prevent="handleResizePointerDown('n', $event)"></div>
                <div class="handle handle-s" @pointerdown.stop.prevent="handleResizePointerDown('s', $event)"></div>
                <div class="handle handle-w" @pointerdown.stop.prevent="handleResizePointerDown('w', $event)"></div>
                <div class="handle handle-e" @pointerdown.stop.prevent="handleResizePointerDown('e', $event)"></div>
            </div>
        </div>

        <div 
            v-show="cursor.active && !isToolCrop" 
            ref="cursorProxy" 
            class="cursor" 
            :class="{ 'black': isToolSketch, 'color': isToolMarker || isToolErase }"
            :style="cursorStyle">
        </div>
    </div>
</template>

<script>
import CompositionLayer from '@/components/common/composition/CompositionLayer.vue'

export default {
    name: 'Composition',
    components: { CompositionLayer },
    props: {
        tool: { 
            type: String, 
            default: 'view', 
            validator: v => ['view', 'marker', 'sketch', 'erase', 'crop'].includes(v) 
        },
        isSketch: { 
            type: Boolean, 
            default: false 
        },

        markerSize: { 
            type: Number, 
            default: 5 
        },
        markerColor: { 
            type: String, 
            default: '#0400FF' 
        },

        sketchSize: { 
            type: Number, 
            default: 1 
        },
        sketchColor: { 
            type: String, 
            default: '#000000' 
        },

        eraseSize: { 
            type: Number, 
            default: 5 
        },
        eraseMode: { 
            type: String, 
            default: 'all', 
            validator: v => ['all', 'marker', 'image', 'sketch'].includes(v) 
        },

        defaultResolution: { 
            type: Number, 
            default: 1024 
        },
        
        thumbnailAutomatic: { 
            type: Boolean, 
            default: false 
        },
        thumbnailResolution: { 
            type: Number, 
            default: 196 
        }
    },
    data() {
        return {
            isPointerDown: false,
            activePointerId: null,
            lastPointerPosition: null,
            cursor: { 
                active: false, 
                x: 0, 
                y: 0 
            },

            crop: { 
                dragging: false, 
                dragMode: null, 
                start: { x: 0, y: 0, rect: null }, 
                rect: { x: 0, y: 0, w: 0, h: 0 }, 
                aspect: null, 
                min: 10, 
                handle: null 
            },

            undoStack: [], 
            redoStack: [], 
            historyLimit: 20,

            layerEmpty: { 
                image: true, 
                marker: true, 
                sketch: true 
            },

            thumbnailTimers: {},

            containerSize: { 
                w: this.defaultResolution, 
                h: this.defaultResolution 
            },
             _resizeObserver: null,

            showToolPreview: false, 
            toolPreviewFading: false,
            _toolPreviewTimer: null,
            _toolPreviewFadeTimer: null
        }
    },
    computed: {
        keyToolView: () => 'view', 
        keyToolMarker: () => 'marker', 
        keyToolSketch: () => 'sketch', 
        keyToolErase: () => 'erase', 
        keyToolCrop: () => 'crop',

        isToolView() { 
            return this.tool === this.keyToolView 
        }, 
        isToolSketch() { 
            return this.tool === this.keyToolSketch 
        }, 
        isToolMarker() { 
            return this.tool === this.keyToolMarker 
        }, 
        isToolErase() { 
            return this.tool === this.keyToolErase 
        }, 
        isToolCrop() { 
            return this.tool === this.keyToolCrop 
        },

        isLayerImageEmpty() { 
            return this.layerEmpty.image 
        },
        isLayerMarkerEmpty() { 
            return this.layerEmpty.marker 
        }, 
        isLayerSketchEmpty() { 
            return this.layerEmpty.sketch 
        },

        hasCropRect() { 
            return true 
        },

        containerScale() { 
            const base = this.defaultResolution || 1024
            const m = Math.min(this.containerSize.w, this.containerSize.h)
            return m / base 
        },

        markerCursorSize() { 
            return this._computeCursorSize(12 - this.markerSize)
        },
        sketchCursorSize() {
            return this._computeCursorSize(this.sketchSize)
        },
        eraseCursorSize() { 
            return this._computeCursorSize(10 - this.eraseSize)
        },

        cursorDiameter() { 
            if (this.isToolMarker) return this.markerCursorSize
            if (this.isToolSketch) return this.sketchCursorSize
            if (this.isToolErase) return this.eraseCursorSize
            return 1
        },

        cursorStyle() { 
            return { 
                left: this.cursor.x + 'px', 
                top: this.cursor.y + 'px', 
                width: this.cursorDiameter + 'px', 
                height: this.cursorDiameter + 'px' 
            } 
        },
        toolPreviewStyle() { 
            const size = this.isToolErase ? this.eraseCursorSize : (this.isToolMarker ? this.markerCursorSize : this.cursorDiameter)
            return { 
                width: size + 'px', 
                height: size + 'px', 
                marginLeft: (-size / 2) + 'px', 
                marginTop: (-size / 2) + 'px' 
            } 
        },
        cropRectStyle() { 
            const r = this.crop.rect
            return { 
                left: r.x + 'px', 
                top: r.y + 'px', 
                width: r.w + 'px', 
                height: r.h + 'px' 
            }
        }
    },
    watch: { 
        tool(newValue, oldValue) { 
            if (newValue === oldValue) return
            if (newValue === this.keyToolCrop) this.$nextTick(() => this._resetFullSelection()) 
        }, 
        eraseSize() { 
            if (this.isToolErase && !this.cursor.active) this._showToolPreview() 
        },
        markerSize() {
            if (this.isToolMarker && !this.cursor.active) this._showToolPreview()
        }
    },
    emits: ['tool-change', 'layer-change', 'layer-thumbnail', 'update:eraseSize', 'update:markerSize'],
    methods: {
        _computeCursorSize(size) {
            const logical = size ** 2.6 + 8
            const scaledCurrent = logical * this.containerScale
            const scaledMax = Math.min(this.containerSize.w, this.containerSize.h) * 0.5
            const scaledMin = 4
            return Math.max(scaledMin, Math.min(scaledCurrent, scaledMax))
        },

        _resetFullSelection() {
            const b = this._containerSize()
            this.crop.rect = { x: 0, y: 0, w: b.w, h: b.h }
        },
        invokeTrigger(t) {
            if (!this.isToolCrop) return
            if (t === 'apply') this.applyCrop()
            else if (t === 'reject') this.rejectCrop()
            else if (t === 'zoom-out') this.zoomOut()
            else if (t === 'zoom-in') this.zoomIn()
        },
        handlePointerDown(e) {
            if (this.isToolView || this.isToolCrop) return
            if (e && e.pointerType === 'mouse' && e.button !== 0) return
            if (!e.isPrimary && e.pointerType !== 'mouse') return
            if (this.activePointerId !== null && this.activePointerId !== e.pointerId) return
            this.updateCursorFromEvent(e)
            if (this.activePointerId === null) this.activePointerId = e.pointerId
            this.isPointerDown = true
            this.$refs.container.setPointerCapture(e.pointerId)
            const p = this.getEventPosition(e)
            this.storeSnapshot()
            if (this.isToolSketch) this.applySketchAt(p.x, p.y)
            else if (this.isToolMarker) this.applyMarkerAt(p.x, p.y)
            else if (this.isToolErase) this.applyEraseAt(p.x, p.y)
            this.lastPointerPosition = p
        },
        handlePointerMove(e) {
            if (this.activePointerId !== null && e.pointerId !== this.activePointerId) return
            this.updateCursorFromEvent(e)
            if (this.isToolCrop || !this.isPointerDown) return
            const p = this.getEventPosition(e)
            if (this.isToolSketch) this.applySketchStroke(this.lastPointerPosition, p)
            else if (this.isToolMarker) this.applyMarkerStroke(this.lastPointerPosition, p)
            else if (this.isToolErase) this.applyEraseStroke(this.lastPointerPosition, p)
            this.lastPointerPosition = p
        },
        handlePointerUp(e) {
            if (this.activePointerId !== null && e.pointerId !== this.activePointerId) return
            if (!this.isPointerDown) return
            this.isPointerDown = false
            try { this.$refs.container.releasePointerCapture(e.pointerId) } catch (_) {}
            if (this.isToolErase) this.finalizeErase()
            this.lastPointerPosition = null
            this.activePointerId = null
        },
        handlePointerEnter(e) {
            this.cursor.active = !this.isToolCrop
            this.updateCursorFromEvent(e)
            if (this.cursor.active && this.showToolPreview) this._hideToolPreview(true)
        },
        handlePointerLeave(e) {
            if (this.activePointerId !== null && e.pointerId !== this.activePointerId) return
            this.cursor.active = false
        },
        handleLayerChange(key, payload) {
            const empty = payload && typeof payload.empty === 'boolean' ? payload.empty : false
            this.$set ? this.$set(this.layerEmpty, key, empty) : (this.layerEmpty[key] = empty)
            this.$emit('layer-change', { key, empty })
            this._scheduleThumbnail(key)
        },
        getEventPosition(e) {
            const el = this.$refs.container
            if (!el) return null
            const rect = el.getBoundingClientRect()
            const w = Math.max(1, Math.round(rect.width))
            const h = Math.max(1, Math.round(rect.height))
            const x = Math.max(0, Math.min(w, e.clientX - rect.left))
            const y = Math.max(0, Math.min(h, e.clientY - rect.top))
            return { x, y }
        },
        updateCursorFromEvent(e) {
            const p = this.getEventPosition(e)
            if (!p) return
            this.cursor.x = p.x
            this.cursor.y = p.y
        },
        cropPointerDown(e) {
            const p = this.getEventPosition(e)
            const r = this.crop.rect
            if (!this._pointInRect(p, r)) return
            this.crop.dragMode = 'move'
            this.crop.dragging = true
            this.crop.start = { x: p.x, y: p.y, rect: { ...r } }
            try { this.$refs.cropOverlay.setPointerCapture(e.pointerId) } catch (_) {}
        },
        cropPointerMove(e) {
            if (!this.crop.dragging) return
            const p = this.getEventPosition(e)
            const s = this.crop.start
            if (this.crop.dragMode === 'move') {
                const dx = p.x - s.x
                const dy = p.y - s.y
                let x = s.rect.x + dx
                let y = s.rect.y + dy
                const b = this._containerSize()
                x = Math.max(0, Math.min(b.w - s.rect.w, x))
                y = Math.max(0, Math.min(b.h - s.rect.h, y))
                this.crop.rect = { ...this.crop.rect, x, y }
                return
            }
            if (this.crop.dragMode === 'resize') this._applyResizeFromPointer(p, s)
        },
        cropPointerUp(e) {
            if (!this.crop.dragging) return
            this.crop.dragging = false
            this.crop.dragMode = null
            this.crop.handle = null
            try { this.$refs.cropOverlay.releasePointerCapture(e.pointerId) } catch (_) {}
        },
        handleResizePointerDown(handle, e) {
            const p = this.getEventPosition(e)
            const r = this.crop.rect
            this.crop.dragMode = 'resize'
            this.crop.dragging = true
            this.crop.handle = handle
            this.crop.start = { x: p.x, y: p.y, rect: { ...r } }
            try { this.$refs.cropOverlay.setPointerCapture(e.pointerId) } catch (_) {}
        },
        async applyCrop() {
            if (this.crop.rect.w <= 0 || this.crop.rect.h <= 0) return
            this.storeSnapshot()
            this.redoStack = []
            const n = this._normalizedCropRect()
            for (const k of ['image', 'marker', 'sketch']) {
                const l = this.$refs[k]
                if (l && l.crop) l.crop(n)
            }
            this._resetFullSelection()
            this.$emit('tool-change')
        },
        rejectCrop() {
            this.crop.dragging = false
            this.crop.dragMode = null
            this._resetFullSelection()
            this.$emit('tool-change')
        },
        zoomOut() {
            const f = 0.9
            this.storeSnapshot()
            this.redoStack = []
            ;['image', 'marker', 'sketch'].forEach(k => {
                const l = this.$refs[k]
                if (l) l.scale(f)
            })
        },
        zoomIn() {
            const f = 1.1
            this.storeSnapshot()
            this.redoStack = []
            ;['image', 'marker', 'sketch'].forEach(k => {
                const l = this.$refs[k]
                if (l) l.scale(f)
            })
        },
        _pointInRect(p, r) {
            return p.x >= r.x && p.y >= r.y && p.x <= r.x + r.w && p.y <= r.y + r.h
        },
        _containerSize() {
            const el = this.$refs.container
            const rect = el ? el.getBoundingClientRect() : { width: this.defaultResolution, height: this.defaultResolution }
            return { w: Math.max(1, Math.round(rect.width)), h: Math.max(1, Math.round(rect.height)) }
        },
        _clampRect(r) {
            const b = this._containerSize()
            let x = Math.max(0, Math.min(b.w, r.x))
            let y = Math.max(0, Math.min(b.h, r.y))
            let w = Math.max(this.crop.min, Math.min(b.w - x, r.w))
            let h = Math.max(this.crop.min, Math.min(b.h - y, r.h))
            if (x + w > b.w) x = b.w - w
            if (y + h > b.h) y = b.h - h
            return { x, y, w, h }
        },
        _applyResizeFromPointer(p, start) {
            const handle = this.crop.handle
            if (!handle) return
            const sr = start.rect
            const bounds = this._containerSize()
            const min = this.crop.min
            const right = sr.x + sr.w
            const bottom = sr.y + sr.h
            let x = sr.x
            let y = sr.y
            let w = sr.w
            let h = sr.h
            if (handle === 'nw') {
                x = Math.min(right - min, Math.max(0, p.x))
                y = Math.min(bottom - min, Math.max(0, p.y))
                w = right - x
                h = bottom - y
            } else if (handle === 'ne') {
                y = Math.min(bottom - min, Math.max(0, p.y))
                const nr = Math.max(sr.x + min, Math.min(bounds.w, p.x))
                w = nr - sr.x
                h = bottom - y
            } else if (handle === 'sw') {
                x = Math.min(right - min, Math.max(0, p.x))
                const nb = Math.max(sr.y + min, Math.min(bounds.h, p.y))
                w = right - x
                h = nb - sr.y
            } else if (handle === 'se') {
                const nr = Math.max(sr.x + min, Math.min(bounds.w, p.x))
                const nb = Math.max(sr.y + min, Math.min(bounds.h, p.y))
                w = nr - sr.x
                h = nb - sr.y
            } else if (handle === 'n') {
                y = Math.min(bottom - min, Math.max(0, p.y))
                h = bottom - y
            } else if (handle === 's') {
                const nb = Math.max(sr.y + min, Math.min(bounds.h, p.y))
                h = nb - sr.y
            } else if (handle === 'w') {
                x = Math.min(right - min, Math.max(0, p.x))
                w = right - x
            } else if (handle === 'e') {
                const nr = Math.max(sr.x + min, Math.min(bounds.w, p.x))
                w = nr - sr.x
            }
            this.crop.rect = this._clampRect({ x, y, w, h })
        },
        _normalizedCropRect() {
            const b = this._containerSize()
            const r = this.crop.rect
            return { x: r.x / b.w, y: r.y / b.h, w: r.w / b.w, h: r.h / b.h }
        },
        exportState() {
            try {
                return {
                    image: this.getLayerImage(),
                    marker: this.getLayerMarker(),
                    sketch: this.getLayerSketch(),
                    undoStack: JSON.parse(JSON.stringify(this.undoStack)),
                    redoStack: JSON.parse(JSON.stringify(this.redoStack)),
                    layerEmpty: { ...this.layerEmpty }
                }
            } catch (_) {
                return null
            }
        },
        async importState(s) {
            if (!s) return
            const img = s.image
            if (img) await this.$refs.image.fromDataURL(img, { scan: true })
            else this.$refs.image.clear()
            if (s.marker) await this.$refs.marker.fromDataURL(s.marker, { scan: true })
            else this.$refs.marker.clear()
            if (s.sketch) await this.$refs.sketch.fromDataURL(s.sketch, { scan: true })
            else this.$refs.sketch.clear()
            this.undoStack = Array.isArray(s.undoStack) ? JSON.parse(JSON.stringify(s.undoStack)) : []
            this.redoStack = Array.isArray(s.redoStack) ? JSON.parse(JSON.stringify(s.redoStack)) : []
            if (s.layerEmpty) this.layerEmpty = { ...this.layerEmpty, ...s.layerEmpty }
        },
        storeSnapshot() {
            const snap = this.snapshot()
            this.undoStack.push(snap)
            if (this.undoStack.length > this.historyLimit) this.undoStack.shift()
            this.redoStack = []
        },
        async restoreSnapshot(snap) {
            if (snap.image) await this.$refs.image.fromDataURL(snap.image, { scan: true })
            if (snap.marker) await this.$refs.marker.fromDataURL(snap.marker, { scan: true })
            if (snap.sketch) await this.$refs.sketch.fromDataURL(snap.sketch, { scan: true })
        },
        snapshot() {
            return { image: this.getLayerImage(), marker: this.getLayerMarker(), sketch: this.getLayerSketch() }
        },
        async init(imageDataUrl, markerDataUrl, sketchDataUrl) {
            if (imageDataUrl) await this.$refs.image.fromDataURL(imageDataUrl, { scan: true })
            else this.$refs.image.clear()
            if (markerDataUrl) await this.$refs.marker.fromDataURL(markerDataUrl, { scan: true })
            else this.$refs.marker.clear()
            if (sketchDataUrl) await this.$refs.sketch.fromDataURL(sketchDataUrl, { scan: true })
            else this.$refs.sketch.clear()
            this.undoStack = []
            this.redoStack = []
            this.storeSnapshot()
        },
        async updateImage(d) {
            this.storeSnapshot()
            this.redoStack = []
            if (d) await this.$refs.image.fromDataURL(d, { scan: true })
            else this.$refs.image.clear()
        },
        async updateMarker(d) {
            this.storeSnapshot()
            this.redoStack = []
            if (d) await this.$refs.marker.fromDataURL(d, { scan: true })
            else this.$refs.marker.clear()
        },
        async updateSketch(d) {
            this.storeSnapshot()
            this.$refs.sketch.clear()
            this.redoStack = []
            if (d) await this.$refs.sketch.fromDataURL(d, { scan: true })
            else this.$refs.sketch.clear()
        },
        async applyTransparency(layerKey, dataUrl, options = {}) {
            if (!dataUrl) return
            const layer = this.$refs[layerKey]
            if (!layer || typeof layer.applyTransparency !== 'function') return
            this.storeSnapshot()
            this.redoStack = []
            await layer.applyTransparency(dataUrl, options)
        },
        async reset() {
            this.storeSnapshot()
            this.$refs.image.clear()
            this.$refs.marker.clear()
            this.$refs.sketch.clear()
            this.redoStack = []
        },
        undo() {
            if (this.undoStack.length <= 1) return
            const current = this.snapshot()
            this.redoStack.push(current)
            const snap = this.undoStack.pop()
            this.restoreSnapshot(snap)
        },
        redo() {
            if (!this.redoStack.length) return
            const snap = this.redoStack.pop()
            const current = this.snapshot()
            this.undoStack.push(current)
            this.restoreSnapshot(snap)
        },
        async rotate(clockwise = true) {
            this.storeSnapshot()
            this.redoStack = []
            this.$refs.image.rotate(clockwise)
            this.$refs.marker.rotate(clockwise)
            this.$refs.sketch.rotate(clockwise)
        },
        render(opts = {}) {
            const res = opts.resolution || this.defaultResolution
            const bg = opts.background || null
            let list = Array.isArray(opts.layers) ? opts.layers.map(k => k === 'base' ? 'image' : k).filter(k => ['image', 'marker', 'sketch'].includes(k)) : null
            if (list === null) list = ['image', 'marker', 'sketch']
            // Allow optional per-layer opacity without mutating underlying layer buffers.
            const layerOpacities = (opts.layerOpacities && typeof opts.layerOpacities === 'object') ? opts.layerOpacities : null
            if (res === this.defaultResolution && !bg && list.length === 1) return this._safeLayerDataURL(list[0])
            const canvas = document.createElement('canvas')
            canvas.width = res
            canvas.height = res
            const ctx = canvas.getContext('2d')
            ctx.imageSmoothingEnabled = true
            if (bg) {
                ctx.fillStyle = bg
                ctx.fillRect(0, 0, res, res)
            }
            const drawLayer = k => {
                const l = this.$refs[k]
                if (!l) return
                const b = l.getBuffer ? l.getBuffer() : null
                if (!b) return
                const alpha = layerOpacities && typeof layerOpacities[k] === 'number' ? Math.max(0, Math.min(1, layerOpacities[k])) : 1
                if (alpha !== 1) ctx.save()
                if (alpha !== 1) ctx.globalAlpha = alpha
                if (b.width === res) ctx.drawImage(b, 0, 0)
                else ctx.drawImage(b, 0, 0, res, res)
                if (alpha !== 1) ctx.restore()
            }
            for (const k of list) {
                if (k === 'image') drawLayer('image')
                else if (k === 'marker') drawLayer('marker')
                else if (k === 'sketch') drawLayer('sketch')
            }
            return canvas.toDataURL('image/png')
        },
        renderImage(r) {
            return this.render({ resolution: r || this.defaultResolution, background: '#ffffff', layers: ['image'] })
        },
        renderSketch(r) {
            return this.render({ resolution: r || this.defaultResolution, background: '#ffffff', layers: ['sketch'] })
        },
        renderMarker(r) {
            return this.render({ resolution: r || this.defaultResolution, background: '#ffffff', layers: ['marker'] })
        },
        renderComposite(r) {
            return this.render({ resolution: r || this.defaultResolution, layerOpacities: { marker: 0.5 } })
        },

        getLayerImage() { return this._safeLayerDataURL('image') },
        getLayerMarker() { return this._safeLayerDataURL('marker') },
        getLayerSketch() { return this._safeLayerDataURL('sketch') },
        applyMarkerAt(x, y) {
            this.applyMarkerStroke({ x, y }, { x: x + 0.01, y: y + 0.01 })
        },
        applyMarkerStroke(p1, p2) {
            const l = this.$refs.marker
            if (!l) return
            l.withCssContext(c => {
                c.lineCap = 'round'
                c.lineJoin = 'round'
                c.strokeStyle = this.markerColor
                c.lineWidth = this.markerCursorSize
                c.beginPath()
                c.moveTo(p1.x, p1.y)
                c.lineTo(p2.x, p2.y)
                c.stroke()
            })
        },
        applySketchAt(x, y) {
            this.applySketchStroke({ x, y }, { x: x + 0.01, y: y + 0.01 })
        },
        applySketchStroke(p1, p2) {
            this.$refs.sketch.withCssContext(c => {
                c.lineCap = 'round'
                c.lineJoin = 'round'
                c.strokeStyle = this.sketchColor
                c.lineWidth = this.sketchCursorSize
                c.beginPath()
                c.moveTo(p1.x, p1.y)
                c.lineTo(p2.x, p2.y)
                c.stroke()
            })
        },
        applyEraseAt(x, y) {
            const layers = this.eraseTargets()
            for (const l of layers) {
                if (!l) continue
                l.withCssContext(c => {
                    c.save()
                    c.beginPath()
                    c.arc(x, y, this.eraseCursorSize / 2, 0, Math.PI * 2)
                    c.globalCompositeOperation = 'destination-out'
                    c.fill()
                    c.restore()
                }, { markDirty: true })
            }
        },
        applyEraseStroke(p1, p2) {
            const layers = this.eraseTargets()
            for (const l of layers) {
                if (!l) continue
                l.withCssContext(c => {
                    c.save()
                    c.lineCap = 'round'
                    c.lineJoin = 'round'
                    c.lineWidth = this.eraseCursorSize
                    c.beginPath()
                    c.moveTo(p1.x, p1.y)
                    c.lineTo(p2.x, p2.y)
                    c.globalCompositeOperation = 'destination-out'
                    c.stroke()
                    c.restore()
                }, { markDirty: true })
            }
        },
        eraseTargets() {
            const layerFor = key => {
                const layer = this.$refs[key]
                if (!layer) return null
                const emptyByState = this.layerEmpty[key] === true
                const emptyByRef = typeof layer.empty === 'boolean' ? layer.empty : null
                if (emptyByState || emptyByRef) return null
                return layer
            }

            const sketch = layerFor('sketch')
            const image = layerFor('image')
            const marker = layerFor('marker')

            let targets = []
            if (this.isSketch) targets = [sketch]
            else if (this.eraseMode === 'all') targets = [image, marker, sketch]
            else if (this.eraseMode === 'image') targets = [image]
            else if (this.eraseMode === 'marker') targets = [marker]
            else if (this.eraseMode === 'sketch') targets = [sketch]

            return targets.filter(Boolean)
        },
        finalizeErase() {
            const layers = this.eraseTargets()
            const scan = () => layers.forEach(l => l ? l.scan() : null)
            if (window.requestIdleCallback) requestIdleCallback(scan)
            else setTimeout(scan, 0)
        },
        _safeLayerDataURL(key) {
            try {
                const l = this.$refs[key]
                if (!l) return this._emptyDataURL(this.defaultResolution, null)
                return l.toDataURL()
            } catch (_) {
                return this._emptyDataURL(this.defaultResolution, null)
            }
        },
        _emptyDataURL(res, bg) {
            const size = res || this.defaultResolution
            const c = document.createElement('canvas')
            c.width = size
            c.height = size
            if (bg) {
                const ctx = c.getContext('2d')
                ctx.fillStyle = bg
                ctx.fillRect(0, 0, size, size)
            }
            return c.toDataURL('image/png')
        },
        _scheduleThumbnail(key) {
            if (!this.thumbnailAutomatic) return
            const d = 100
            if (this.thumbnailTimers[key]) clearTimeout(this.thumbnailTimers[key])
            this.thumbnailTimers[key] = setTimeout(() => {
                this.thumbnailTimers[key] = null
                this._emitThumbnail(key)
            }, d)
        },
        _emitThumbnail(key) {
            if (this.layerEmpty[key]) {
                this.$emit('layer-thumbnail', { key, dataUrl: null })
                return
            }
            const l = this.$refs[key]
            const r = this.thumbnailResolution || 256
            const dataUrl = l ? l.toDataURL({ resolution: r }) : this._emptyDataURL(r, null)
            this.$emit('layer-thumbnail', { key, dataUrl })
        },
        _hideToolPreview(immediate = false) {
            if (immediate) {
                this.showToolPreview = false
                this.toolPreviewFading = false
            } else {
                this.toolPreviewFading = true
                if (this._toolPreviewFadeTimer) clearTimeout(this._toolPreviewFadeTimer)
                this._toolPreviewFadeTimer = setTimeout(() => {
                    this.showToolPreview = false
                    this.toolPreviewFading = false
                    this._toolPreviewFadeTimer = null
                }, 250)
            }
            if (this._toolPreviewTimer) { clearTimeout(this._toolPreviewTimer); this._toolPreviewTimer = null }
        },
        _showToolPreview() {
            if (!(this.isToolErase || this.isToolMarker)) return
            if (this.cursor.active) return
            if (this._toolPreviewTimer) { clearTimeout(this._toolPreviewTimer); this._toolPreviewTimer = null }
            if (this._toolPreviewFadeTimer) { clearTimeout(this._toolPreviewFadeTimer); this._toolPreviewFadeTimer = null }
            this.toolPreviewFading = false
            this.showToolPreview = true
            this._toolPreviewTimer = setTimeout(() => {
                this._hideToolPreview()
            }, 1500)
        }
    },
    mounted() { 
        this.storeSnapshot()
        if (this.isToolCrop) this.$nextTick(() => this._resetFullSelection())
        this.$nextTick(() => { 
            const el = this.$refs.container
            if (el && 'ResizeObserver' in window) { 
                this._resizeObserver = new ResizeObserver(entries => { 
                    for (const entry of entries) { 
                        const r = entry.contentRect
                         this.containerSize = { w: r.width, h: r.height } 
                    } 
                })
                this._resizeObserver.observe(el) 
            } else { 
                const rect = el ? el.getBoundingClientRect() : { width: this.defaultResolution, height: this.defaultResolution };
                this.containerSize = { w: rect.width, h: rect.height }
            }
        })
    },
    beforeUnmount() { 
        if (this._resizeObserver) { 
            try { 
                this._resizeObserver.disconnect() 
            } catch (_) { } 
            this._resizeObserver = null 
        } 
        if (this._toolPreviewTimer) { 
            clearTimeout(this._toolPreviewTimer)
            this._toolPreviewTimer = null 
        }
        if (this._toolPreviewFadeTimer) { 
            clearTimeout(this._toolPreviewFadeTimer)
            this._toolPreviewFadeTimer = null 
        }
    }
}
</script>

<style scoped>
.composition {
    position: relative;
    width: 100%;
    height: 100%;
    touch-action: none;
    user-select: none;
}

.composition.crosshair {
    cursor: crosshair;
}

.composition .layer {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
}

.composition .layer.image {
    z-index: 1;
    pointer-events: none;
}

.composition .layer.marker {
    z-index: 2;
    opacity: 0.5;
}

.composition .layer.sketch {
    z-index: 3;
}

.crop-overlay {
    position: absolute;
    inset: 0;
    z-index: 50;
}

.crop-rect {
    position: absolute;
    box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.45);
    background: rgba(var(--v-theme-color1), 0.1);
}

.crop-rect .grid {
    position: absolute;
    inset: 0;
    border: 1px solid rgba(var(--v-theme-color1), 0.7);
    pointer-events: none;
}

.crop-rect .handle {
    position: absolute;
    width: 20px;
    height: 20px;
    background: #fff;
    border: 2px solid #000;
    border-radius: 4px;
    box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.15);
    transform: translate(-50%, -50%);
    cursor: pointer;
    z-index: 2;
    touch-action: none;
}

.crop-rect .handle::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 40px;
    height: 40px;
    transform: translate(-50%, -50%);
    border-radius: 50%;
    background: transparent;
}

.crop-rect .handle-nw {
    top: 0%;
    left: 0%;
    cursor: nwse-resize;
}

.crop-rect .handle-ne {
    top: 0%;
    left: 100%;
    cursor: nesw-resize;
}

.crop-rect .handle-sw {
    top: 100%;
    left: 0%;
    cursor: nesw-resize;
}

.crop-rect .handle-se {
    top: 100%;
    left: 100%;
    cursor: nwse-resize;
}

.crop-rect .handle-n {
    top: 0%;
    left: 50%;
    cursor: ns-resize;
}

.crop-rect .handle-s {
    top: 100%;
    left: 50%;
    cursor: ns-resize;
}

.crop-rect .handle-w {
    top: 50%;
    left: 0%;
    cursor: ew-resize;
}

.crop-rect .handle-e {
    top: 50%;
    left: 100%;
    cursor: ew-resize;
}

.composition .cursor {
    position: absolute;
    border-radius: 50%;
    pointer-events: none;
    transform: translate(-50%, -50%);
    z-index: 51;
}

.composition .cursor.black {
    background-color: black;
}

.composition .cursor.color {
    background-color: rgb(var(--v-theme-color1));
}

.tool-preview {
    position: absolute;
    left: 50%;
    top: 50%;
    border-radius: 50%;
    background: rgb(var(--v-theme-color1));
    opacity: 0.55;
    pointer-events: none;
    z-index: 60;
    box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.25), 0 0 6px rgba(0, 0, 0, 0.35) inset;
    transition: opacity 0.25s ease;
}

.tool-preview.fade-out {
    opacity: 0;
}
</style>